import { FC } from 'react'

import styled from 'styled-components'
import { Accordion } from '@mantine/core'
import { useForm, Controller } from 'react-hook-form'

import { Button } from 'src/Components/Button'
import { Input } from 'src/Components/Input/index.tsx'
import { Select } from 'src/Components/Select'
import { AddClientBody } from 'src/Redux/api/clientsApi.ts'
import { Modal } from 'src/Components/Modal'
import { organizationalFormOptions } from 'src/Helpers/organizationalForm'

const ModalContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  padding: 20px 0;
  width: 100%;
`

const StyledInput = styled(Input)`
  width: 100%;
`

const InputContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`

const StyledAccordion = styled(Accordion)`
  .mantine-Accordion-content {
    padding-top: 10px !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  .mantine-Accordion-label {
    margin-bottom: 10px !important;
  }
`

interface AddClientModalProps {
  onClose: () => void
  onAddClient: (client: AddClientBody) => void
  isOpened: boolean
}

export const AddClientModal: FC<AddClientModalProps> = ({
  isOpened,
  onClose,
  onAddClient,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<AddClientBody>({
    defaultValues: {
      name: '',
      inn: '',
      director: '',
      legalAddress: '',
      phone: '',
      email: '',
      organizationalForm: '',
      ogrn: undefined,
      headPosition: '',
      headPositionGenitive: '',
      headNameGenitive: '',
      offerContactName: '',
      offerContactPosition: '',
      authorityType: 'Устава',
      kpp: '',
      rs: '',
      ks: '',
      bic: '',
      bankName: '',
      postAddress: '',
    },
  })

  const onSubmit = (data: AddClientBody): void => {
    const processedData = {
      ...data,
      authorityType: data.authorityType
        ? data.authorityType.charAt(0).toUpperCase() +
          data.authorityType.slice(1)
        : 'Устава',
    }
    onAddClient(processedData)
    reset()
    onClose()
  }

  return (
    <Modal opened={isOpened} onClose={onClose} title='Добавить клиента'>
      <ModalContainer>
        <InputContainer>
          <Controller
            rules={{ required: 'Орг. форма обязательна' }}
            name='organizationalForm'
            control={control}
            render={({ field }) => (
              <Select
                placeholder='Выберите орг. форму'
                label='Орг. форма'
                error={errors.organizationalForm?.message}
                data={organizationalFormOptions}
                {...field}
              />
            )}
          />

          <Controller
            name='name'
            control={control}
            rules={{ required: 'Название обязательно' }}
            render={({ field }) => (
              <StyledInput
                placeholder='Руснедра Менеджмент'
                label='Наименование'
                error={errors.name?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='inn'
            control={control}
            rules={{ required: 'ИНН обязателен' }}
            render={({ field }) => (
              <StyledInput
                placeholder='ИНН'
                label='ИНН'
                error={errors.inn?.message}
                {...field}
              />
            )}
          />

          <StyledAccordion>
            <Accordion.Item value='optional-fields'>
              <Accordion.Control>Дополнительные поля</Accordion.Control>
              <Accordion.Panel>
                <InputContainer>
                  <Controller
                    name='ogrn'
                    control={control}
                    render={({ field }) => (
                      <StyledInput
                        placeholder='ОГРН'
                        label='ОГРН'
                        error={errors.ogrn?.message}
                        {...field}
                      />
                    )}
                  />

                  <Controller
                    name='kpp'
                    control={control}
                    render={({ field }) => (
                      <StyledInput
                        placeholder='КПП'
                        label='КПП'
                        error={errors.kpp?.message}
                        {...field}
                      />
                    )}
                  />

                  <Controller
                    name='rs'
                    control={control}
                    render={({ field }) => (
                      <StyledInput
                        placeholder='Расчётный счет'
                        label='Расчётный счет'
                        error={errors.rs?.message}
                        {...field}
                      />
                    )}
                  />

                  <Controller
                    name='ks'
                    control={control}
                    render={({ field }) => (
                      <StyledInput
                        placeholder='Корреспондентский счет'
                        label='Кор. счет'
                        error={errors.ks?.message}
                        {...field}
                      />
                    )}
                  />

                  <Controller
                    name='bic'
                    control={control}
                    render={({ field }) => (
                      <StyledInput
                        placeholder='БИК'
                        label='БИК'
                        error={errors.bic?.message}
                        {...field}
                      />
                    )}
                  />

                  <Controller
                    name='bankName'
                    control={control}
                    render={({ field }) => (
                      <StyledInput
                        placeholder='Название банка'
                        label='Название банка'
                        error={errors.bankName?.message}
                        {...field}
                      />
                    )}
                  />

                  <Controller
                    name='postAddress'
                    control={control}
                    render={({ field }) => (
                      <StyledInput
                        placeholder='Почтовый адрес'
                        label='Почтовый адрес'
                        error={errors.postAddress?.message}
                        {...field}
                      />
                    )}
                  />

                  <Controller
                    name='headPosition'
                    control={control}
                    render={({ field }) => (
                      <StyledInput
                        placeholder='Должность руководителя'
                        label='Должность'
                        error={errors.headPosition?.message}
                        {...field}
                      />
                    )}
                  />

                  <Controller
                    name='director'
                    control={control}
                    render={({ field }) => (
                      <StyledInput
                        placeholder='ФИО руководителя'
                        error={errors.director?.message}
                        label='ФИО руководителя'
                        {...field}
                      />
                    )}
                  />

                  <Controller
                    name='headPositionGenitive'
                    control={control}
                    render={({ field }) => (
                      <StyledInput
                        placeholder='Должность в родительном падеже (генерального директора)'
                        label='Должность (род. падеж)'
                        error={errors.headPositionGenitive?.message}
                        {...field}
                      />
                    )}
                  />

                  <Controller
                    name='headNameGenitive'
                    control={control}
                    render={({ field }) => (
                      <StyledInput
                        placeholder='в лице Кудрявцевой Дарьи Николаевны'
                        label='ФИО (род. падеж)'
                        error={errors.headNameGenitive?.message}
                        {...field}
                      />
                    )}
                  />

                  <Controller
                    name='offerContactPosition'
                    control={control}
                    render={({ field }) => (
                      <StyledInput
                        placeholder='Генеральному директору'
                        label='Должность для КП (дат. падеж)'
                        {...field}
                      />
                    )}
                  />

                  <Controller
                    name='offerContactName'
                    control={control}
                    render={({ field }) => (
                      <StyledInput
                        placeholder='Иванову Ивану Ивановичу'
                        label='ФИО для КП (дат. падеж)'
                        {...field}
                      />
                    )}
                  />

                  <Controller
                    name='authorityType'
                    control={control}
                    render={({ field }) => (
                      <StyledInput
                        placeholder='Устава, Доверенности и т.д. (род. падеж)'
                        label='Действует на основании'
                        error={errors.authorityType?.message}
                        {...field}
                      />
                    )}
                  />

                  <Controller
                    name='legalAddress'
                    control={control}
                    render={({ field }) => (
                      <StyledInput
                        placeholder='Юридический адрес'
                        label='Юридический адрес'
                        error={errors.legalAddress?.message}
                        {...field}
                      />
                    )}
                  />

                  <Controller
                    name='phone'
                    control={control}
                    render={({ field }) => (
                      <StyledInput
                        placeholder='Телефон'
                        label='Телефон'
                        error={errors.phone?.message}
                        {...field}
                      />
                    )}
                  />

                  <Controller
                    name='email'
                    control={control}
                    render={({ field }) => (
                      <StyledInput
                        placeholder='example@company.com'
                        label='Email'
                        error={errors.email?.message}
                        {...field}
                      />
                    )}
                  />
                </InputContainer>
              </Accordion.Panel>
            </Accordion.Item>
          </StyledAccordion>
        </InputContainer>
      </ModalContainer>
      <ButtonContainer>
        <Button onClick={handleSubmit(onSubmit)}>Добавить</Button>
      </ButtonContainer>
    </Modal>
  )
}
